// アプリ全体の状態（§5.4 の再計算分離 / §6.2 アクティブリーグ）。
//
// 不変条件の担保: 重いモンテカルロ（computer.compute）は cost/steady/time/drops/rate が
// 変わった時のみ実行する。**現在の軍資金 B と表示単位の変更では compute を呼ばない**
// （専用 setter が recompute を経由しない）。これが B 非依存 / 単位切替で再シミュしない仕組み。
import type { Poe2ScoutLeague } from '../api/types';
import type { RateTable } from '../currency/rate-table';
import type { EngineResult } from '../core/types';
import { expandSet } from '../model/expand';
import type { FarmSet } from '../store/schema';
import type { Computer } from '../worker-thread/computer';

export interface ComputeState {
  result: EngineResult | null;
  warnings: string[];
  computing: boolean;
}

type Listener = () => void;

export class AppStore {
  leagues: Poe2ScoutLeague[] = [];
  activeLeague: string | null = null;
  rateTable: RateTable | null = null;
  currentSet: FarmSet;
  compute: ComputeState = { result: null, warnings: [], computing: false };

  private readonly computer: Computer;
  private readonly debounceMs: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private mcSignature = '';
  private reqToken = 0;
  private readonly listeners = new Set<Listener>();

  constructor(computer: Computer, initialSet: FarmSet, debounceMs = 0) {
    this.computer = computer;
    this.currentSet = initialSet;
    this.activeLeague = initialSet.league;
    this.debounceMs = debounceMs;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  setLeagues(leagues: Poe2ScoutLeague[]): void {
    this.leagues = leagues;
    this.emit();
  }

  /** レート表をセット/更新（取得・再取得・リーグ切替）。MC を再計算する。 */
  setRateTable(rt: RateTable): void {
    this.rateTable = rt;
    this.recompute(true);
    this.emit();
  }

  /** セットを読込（リーグも合わせる, §8）。MC を再計算する。 */
  loadSet(set: FarmSet): void {
    this.currentSet = set;
    this.activeLeague = set.league;
    this.recompute(true);
    this.emit();
  }

  /** MC 関連入力の変更（cost/drop/steady/time/name 等）。署名が変わった時のみ再計算。 */
  updateSet(mutator: (s: FarmSet) => void): void {
    mutator(this.currentSet);
    this.currentSet.updatedAt = Date.now();
    this.recompute(false);
    this.emit();
  }

  /** 表示単位の変更（§5.6）。再シミュレーションしない。 */
  setDisplayUnit(unit: string): void {
    this.currentSet.displayUnit = unit;
    this.emit();
  }

  /** 現在の軍資金（§5.1）。判定表示のみ。再シミュレーションしない。 */
  setCurrentBankroll(b: number | undefined): void {
    this.currentSet.currentBankroll = b;
    this.emit();
  }

  // MC に影響する入力だけの署名（displayUnit / currentBankroll / name は含めない）。
  private signature(): string {
    const s = this.currentSet;
    return JSON.stringify({
      league: this.rateTable?.league,
      fetchedAt: this.rateTable?.fetchedAt,
      time: s.time,
      steady: s.steady,
      cost: s.costLines,
      drops: s.dropLines,
    });
  }

  private recompute(force: boolean): void {
    const sig = this.signature();
    // 署名が変わっていなければ（in-flight 含め）再計算しない。初期 mcSignature='' で初回は必ず走る。
    if (!force && sig === this.mcSignature) return;
    this.mcSignature = sig;
    if (!this.rateTable) return;
    // タイピング中の連打は 280ms デバウンス（force=読込/再取得 は即時）。
    if (force || this.debounceMs <= 0) {
      this.launch();
      return;
    }
    this.compute = { ...this.compute, computing: true };
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.launch(), this.debounceMs);
  }

  private launch(): void {
    if (!this.rateTable) return;
    const { input, warnings } = expandSet(this.currentSet, this.rateTable);
    const token = ++this.reqToken;
    this.compute = { result: this.compute.result, warnings, computing: true };
    this.computer
      .compute(input)
      .then((result) => {
        if (token !== this.reqToken) return; // 古い結果は破棄
        this.compute = { result, warnings, computing: false };
        this.emit();
      })
      .catch(() => {
        if (token !== this.reqToken) return;
        this.compute = { ...this.compute, computing: false };
        this.emit();
      });
  }
}
